import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Server, Users, Activity, AlertTriangle, RefreshCw, Loader2, ShieldBan, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { tokenStore } from "@/lib/api";
import { toast } from "sonner";

// Entropy pool chart data — static simulation (real device telemetry not exposed per-minute)
const ENTROPY_FLOW = [
  { time: "00:00", poolLevel: 92 }, { time: "04:00", poolLevel: 88 }, { time: "08:00", poolLevel: 78 },
  { time: "12:00", poolLevel: 65 }, { time: "16:00", poolLevel: 82 }, { time: "20:00", poolLevel: 91 }, { time: "Now", poolLevel: 87 },
];

const SERVICES = [
  { name: "Entropy Proxy (AWS)", latency: "1.2ms" },
  { name: "DRBG Engine", latency: "0.3ms" },
  { name: "VRF Service", latency: "2.8ms" },
  { name: "RaaS API Gateway", latency: "0.8ms" },
  { name: "VPN/DX Link", latency: "4.1ms" },
  { name: "Redis Cache", latency: "0.2ms" },
];

const statusDot = (s) => s === "healthy" ? "bg-emerald-500" : s === "warning" ? "bg-amber-500" : "bg-red-500";

export default function SystemOverview() {
  const { t } = useLanguage();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [blockedIPs, setBlockedIPs] = useState(new Set());
  const [blockingIP, setBlockingIP] = useState(null);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/overview`, {
        headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOverview(data);
        setLastUpdated(new Date());
      }
    } catch { /* keep existing data */ }
    finally { setLoading(false); }
  }, []);

  // Load existing blocked IPs on mount so BLOCKED state persists across reloads
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/blocked-ips`, {
          headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
        });
        if (res.ok) {
          const data = await res.json();
          setBlockedIPs(new Set(data.map(d => d.ip)));
        }
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    fetchOverview();
    const interval = setInterval(fetchOverview, 30000);
    return () => clearInterval(interval);
  }, [fetchOverview]);

  const handleBlockIP = useCallback(async (ip) => {
    if (!ip || blockedIPs.has(ip)) return;
    setBlockingIP(ip);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/block-ip`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenStore.getAccess()}` },
        body: JSON.stringify({ ip, reason: "Blocked via Security Alerts dashboard" }),
      });
      if (!res.ok) throw new Error("Failed to block IP");
      setBlockedIPs(prev => new Set([...prev, ip]));
      toast.success(`IP ${ip} has been blocked`);
    } catch {
      toast.error(`Failed to block IP ${ip}`);
    } finally {
      setBlockingIP(null);
    }
  }, [blockedIPs]);

  const stats = overview ? [
    {
      label: "QRNG Devices",
      value: String(overview.devices.total),
      sub: `${overview.devices.healthy} healthy, ${overview.devices.offline} offline`,
      icon: Server,
      color: "text-cyan-600",
      bg: "bg-cyan-50",
    },
    {
      label: "Active Tenants",
      value: String(overview.tenants.active),
      sub: `+${overview.tenants.new_this_month} this month`,
      icon: Users,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      label: "API Calls (Month)",
      value: overview.api_calls.current_month > 999999
        ? `${(overview.api_calls.current_month / 1000000).toFixed(1)}M`
        : overview.api_calls.current_month.toLocaleString(),
      sub: `${overview.api_keys.active} active API keys`,
      icon: Activity,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      label: "Security Alerts",
      value: String(overview.audit.failed_today),
      sub: `${overview.audit.total_events.toLocaleString()} total audit events`,
      icon: AlertTriangle,
      color: "text-amber-600",
      bg: "bg-amber-50",
    },
  ] : [];

  return (
    <div data-testid="system-overview-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="system-overview-title">{t('system.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">Global health dashboard for QRNG infrastructure and SaaS services</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-[11px] text-slate-400 flex items-center gap-1.5">
              <Activity className="w-3 h-3" /> Live · {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchOverview}
            disabled={loading}
            className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-xs px-4 py-2 disabled:opacity-50"
            data-testid="refresh-overview-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Global Stats */}
      {loading && !overview ? (
        <div className="flex items-center justify-center py-12 text-slate-400 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading system metrics...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="admin-stats">
            {stats.map((stat, i) => (
              <div key={i} className="bg-white border border-slate-200 p-5" data-testid={`admin-stat-${i}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 ${stat.bg} flex items-center justify-center`}>
                    <stat.icon className={`w-4 h-4 ${stat.color}`} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">{stat.label}</div>
                    <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{stat.sub}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-4 mb-6">
            {/* Entropy Pool Chart */}
            <div className="lg:col-span-2 bg-white border border-slate-200 p-6" data-testid="entropy-pool-chart">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Entropy Pool Level</h3>
                  <p className="text-xs text-slate-500 mt-0.5">AWS-side entropy buffer status (last 24h)</p>
                </div>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-mono">
                  {overview?.devices?.avg_entropy ?? 87}% AVG
                </Badge>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={ENTROPY_FLOW}>
                  <defs>
                    <linearGradient id="poolGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#06B6D4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="time" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
                  <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 0, fontSize: 12 }} formatter={v => [`${v}%`, "Pool Level"]} />
                  <Area type="monotone" dataKey="poolLevel" stroke="#06B6D4" strokeWidth={2} fill="url(#poolGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Active Alerts */}
            <div className="bg-white border border-slate-200 p-6" data-testid="active-alerts-panel">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">
                Recent Security Alerts
                {overview?.recent_alerts?.length > 0 && (
                  <Badge className="ml-2 bg-red-100 text-red-700 text-[10px]">{overview.recent_alerts.length}</Badge>
                )}
              </h3>
              <div className="space-y-3">
                {overview?.recent_alerts?.length === 0 ? (
                  <div className="text-xs text-slate-400 text-center py-4">No recent alerts</div>
                ) : overview?.recent_alerts?.map((alert, i) => (
                  <div
                    key={i}
                    className={`p-3 border-l-2 ${alert.severity === "critical" ? "border-l-red-500 bg-red-50/50" : "border-l-amber-500 bg-amber-50/50"}`}
                    data-testid={`active-alert-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[9px] uppercase ${alert.severity === "critical" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                        {alert.severity}
                      </Badge>
                      <span className="text-[10px] text-slate-400">{alert.time}</span>
                      {alert.ip && (
                        <button
                          onClick={() => handleBlockIP(alert.ip)}
                          disabled={blockingIP === alert.ip || blockedIPs.has(alert.ip)}
                          className={`ml-auto flex items-center gap-1 text-[9px] font-medium px-2 py-0.5 rounded transition-colors ${
                            blockedIPs.has(alert.ip)
                              ? "bg-emerald-100 text-emerald-700 cursor-default"
                              : "bg-red-100 text-red-700 hover:bg-red-200 cursor-pointer"
                          } disabled:opacity-60`}
                          data-testid={`block-ip-btn-${i}`}
                          title={blockedIPs.has(alert.ip) ? `${alert.ip} is blocked` : `Block ${alert.ip}`}
                        >
                          {blockedIPs.has(alert.ip) ? (
                            <><Check className="w-2.5 h-2.5" /> BLOCKED</>
                          ) : blockingIP === alert.ip ? (
                            <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Blocking...</>
                          ) : (
                            <><ShieldBan className="w-2.5 h-2.5" /> Block IP</>
                          )}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-slate-700">{alert.message}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Device Grid + Services */}
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Device Health */}
            <div className="bg-white border border-slate-200 p-6" data-testid="device-health-grid">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900">QRNG Device Health</h3>
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />{overview?.devices?.healthy ?? 0} healthy</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />{overview?.devices?.warning ?? 0} warning</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />{overview?.devices?.offline ?? 0} offline</span>
                </div>
              </div>
              <div className="space-y-3">
                {overview?.device_list?.map((d, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 border border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`device-health-${i}`}>
                    <div className={`w-2.5 h-2.5 rounded-full ${statusDot(d.status)}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono font-medium text-slate-900">{d.id}</span>
                        <span className="text-[10px] text-slate-400">{d.location}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-[10px] text-slate-500">Throughput: <span className="text-slate-700 font-medium">{d.throughput}</span></span>
                        <span className="text-[10px] text-slate-500">SP800-90B: <span className={`font-medium ${d.sp90b === "Pass" ? "text-emerald-600" : "text-slate-400"}`}>{d.sp90b}</span></span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-mono text-slate-900">{d.entropy > 0 ? `${d.entropy}%` : "--"}</div>
                      <div className="text-[10px] text-slate-400">entropy</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tenant Stats Row */}
              {overview && (
                <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <div className="text-lg font-bold text-slate-900">{overview.tenants.total}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Total Tenants</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-emerald-600">{overview.tenants.active}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Active</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-amber-600">{overview.tenants.suspended}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Suspended</div>
                  </div>
                </div>
              )}
            </div>

            {/* Service Status */}
            <div className="bg-white border border-slate-200 p-6" data-testid="service-status-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900">Service Status</h3>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">ALL OPERATIONAL</Badge>
              </div>
              <div className="space-y-2">
                {SERVICES.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border border-slate-100" data-testid={`service-status-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                      <span className="text-sm text-slate-700">{s.name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs font-mono text-slate-400">{s.latency}</span>
                      <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200 uppercase">Operational</Badge>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-slate-900">4.1ms</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">DC-AWS Latency</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-slate-900">
                      {overview?.devices?.avg_entropy ?? "--"}%
                    </div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Avg Entropy</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-emerald-600">Healthy</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">VPN/DX Status</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
